import { FocusMonitor, LiveAnnouncer } from '@angular/cdk/a11y';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  TemplateRef,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxControlContext } from '../../base/types';
import { HxButton } from '../button/button';
import { HxDivider } from '../divider/divider';
import { HxIconButton } from '../icon-button/icon-button';
import { HxIcon } from '../icon/icon';

/**
 * HxConfirmationPopup Component
 *
 * Displays a confirmation popup for high-impact actions like delete, archive, or submit. Supports configurable button
 * visibility, custom labels, warning style, and close icon.
 *
 * @example
 *   ```html
 *   <hx-confirmation-popup
 *     [showAcceptButton]="true"
 *     acceptLabel="Yes, Archive"
 *     [showCancelButton]="true"
 *     cancelLabel="Cancel"
 *     [showAsWarning]="true"
 *     (accept)="onAccept()"
 *     (cancel)="onCancel()"
 *   >
 *     Are you sure you want to archive this item?
 *   </hx-confirmation-popup>
 *   ```;
 *
 * @accessibility
 * - WCAG 2.1 Level AA compliant
 * - role="dialog" with proper ARIA attributes
 * - Keyboard navigation: Tab, Enter, Escape
 * - 44×44px minimum touch targets
 */
export type ConfirmationPopupVariant = 'default' | 'warning' | 'success' | 'error' | 'info';

@Component({
  selector: 'hx-confirmation-popup',
  standalone: true,
  imports: [NgTemplateOutlet, HxButton, HxDivider, HxIcon, HxIconButton],
  templateUrl: './confirmation-popup.html',
  styleUrl: './confirmation-popup.css',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'hostClasses()',
  },
})
export class HxConfirmationPopup extends HxBaseControl {
  /** Header text for the popup */
  readonly headerText = input<string>();

  /** Message content for the popup body */
  readonly message = input<string>();

  /** Whether to show the Accept/Confirm button */
  readonly showAcceptButton = input<boolean>(true);

  /** Whether the Accept/Confirm button is disabled */
  readonly acceptDisabled = input<boolean>(false);

  /** Accept button label (default: "Confirm") */
  readonly acceptLabel = input<string>('Confirm');

  /** Whether to show the Cancel button */
  readonly showCancelButton = input<boolean>(true);

  /** Cancel button label (default: "Cancel") */
  readonly cancelLabel = input<string>('Cancel');

  /** Visual variant that determines theme and icon (default, warning, success, error, info) */
  readonly variant = input<ConfirmationPopupVariant>('default');

  /** Show close "X" icon in header */
  readonly showCancelIcon = input<boolean>(false);

  /** Apply variant color styling to popup body */
  readonly applyVariantToBody = input<boolean>(false);

  /** Optional CSS class for custom styling */
  readonly customClass = input<string>();

  /** Optional TemplateRef for header content */
  readonly headerTemplate = input<TemplateRef<any>>();

  /** Optional TemplateRef for body content */
  readonly bodyTemplate = input<TemplateRef<any>>();

  /** Emitted when Accept button is clicked */
  accept = output<void>();

  /** Emitted when Cancel button or Cancel icon is clicked */
  cancel = output<void>();

  /** Emitted when popup is closed without Accept */
  dismiss = output<void>();

  /** Close button element reference for focus monitoring */
  private readonly closeButtonRef = viewChild<ElementRef<HTMLButtonElement>>('closeButton');

  /** CDK services for accessibility */
  private focusMonitor = inject(FocusMonitor);
  private liveAnnouncer = inject(LiveAnnouncer);

  /** Computed effective variant */
  protected readonly effectiveVariant = computed(() => {
    return this.variant();
  });

  /** Computed header icon based on variant */
  protected readonly headerIcon = computed(() => {
    const variant = this.effectiveVariant();
    const iconMap: Record<ConfirmationPopupVariant, string | null> = {
      default: null,
      warning: 'info-circle',
      success: 'check-circle',
      error: 'x-circle',
      info: 'info-circle',
    };
    return iconMap[variant] || null;
  });

  /** Computed CSS classes for styling */
  protected readonly hostClasses = computed(() => {
    const classes = ['hx-confirmation-popup'];
    if (this.customClass()) {
      classes.push(this.customClass()!);
    }
    return classes.join(' ');
  });

  /** Get appropriate button context based on variant */
  protected getAcceptButtonContext(): HxControlContext {
    const variant = this.effectiveVariant();
    const contextMap: Record<ConfirmationPopupVariant, HxControlContext> = {
      default: 'brand',
      warning: 'caution',
      success: 'positive',
      error: 'negative',
      info: 'info',
    };
    return contextMap[variant] || 'brand';
  }

  /** Handle Accept button click */
  protected handleAccept(): void {
    this.accept.emit();
  }

  /** Handle Cancel button or close icon click */
  protected handleCancel(): void {
    this.cancel.emit();
    this.dismiss.emit();
    this.liveAnnouncer.announce('Popup dismissed', 'polite');
  }

  /** Handle Escape key for dismissal */
  protected handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.handleCancel();
    }
  }
}
